import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Shared slug generation utility - used for both creating and matching slugs
export function getPlaybookSlug(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export interface Playbook {
  id: string;
  title: string;
  description: string;
  icon_name: string;
  category: string;
  tier: string;
  estimated_time: string;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface PlaybookNode {
  id: string;
  playbook_id: string;
  node_id: string;
  question: string;
  description: string | null;
  is_end: boolean;
  end_type: string | null;
  tip_text: string | null;
  options: NodeOption[];
  created_at: string;
  updated_at: string;
}

export interface NodeOption {
  label: string;
  nextNode?: string;
  action?: string;
  variant?: "default" | "destructive" | "warning";
}

export function usePlaybooks(includeUnpublished = false) {
  return useQuery({
    queryKey: ["playbooks", includeUnpublished],
    queryFn: async () => {
      let query = supabase
        .from("playbooks")
        .select("*")
        .order("sort_order", { ascending: true });
      
      if (!includeUnpublished) {
        query = query.eq("is_published", true);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as Playbook[];
    },
  });
}

export function usePlaybook(id: string | undefined) {
  return useQuery({
    queryKey: ["playbook", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("playbooks")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as Playbook | null;
    },
    enabled: !!id,
  });
}

export function usePlaybookBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["playbook-slug", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("playbooks")
        .select("*")
        .eq("is_published", true);
      if (error) throw error;
      
      // Find by slug match using the shared slug utility
      const playbook = (data as Playbook[])?.find(p => 
        getPlaybookSlug(p.title) === slug
      );
      return playbook || null;
    },
    enabled: !!slug,
  });
}

export function usePlaybookNodes(playbookId: string | undefined) {
  return useQuery({
    queryKey: ["playbook-nodes", playbookId],
    queryFn: async () => {
      if (!playbookId) return [];
      const { data, error } = await supabase
        .from("playbook_nodes")
        .select("*")
        .eq("playbook_id", playbookId);
      if (error) throw error;
      
      // Parse options from JSONB
      return (data as any[]).map(node => ({
        ...node,
        options: node.options as NodeOption[],
      })) as PlaybookNode[];
    },
    enabled: !!playbookId,
  });
}

export function useCreatePlaybook() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (playbook: Omit<Playbook, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("playbooks")
        .insert(playbook)
        .select()
        .single();
      if (error) throw error;
      return data as Playbook;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playbooks"] });
    },
  });
}

export function useUpdatePlaybook() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Playbook> & { id: string }) => {
      const { data, error } = await supabase
        .from("playbooks")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as Playbook;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["playbooks"] });
      queryClient.invalidateQueries({ queryKey: ["playbook", data.id] });
    },
  });
}

export function useDeletePlaybook() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("playbooks")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playbooks"] });
    },
  });
}

export function useCreateNode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (node: Omit<PlaybookNode, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("playbook_nodes")
        .insert({
          ...node,
          options: node.options as any,
        })
        .select()
        .single();
      if (error) throw error;
      return {
        ...data,
        options: data.options as unknown as NodeOption[],
      } as PlaybookNode;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["playbook-nodes", data.playbook_id] });
    },
  });
}

export function useUpdateNode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PlaybookNode> & { id: string }) => {
      const { data, error } = await supabase
        .from("playbook_nodes")
        .update({
          ...updates,
          options: updates.options as any,
        })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return {
        ...data,
        options: data.options as unknown as NodeOption[],
      } as PlaybookNode;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["playbook-nodes", data.playbook_id] });
    },
  });
}

export function useDeleteNode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, playbookId }: { id: string; playbookId: string }) => {
      const { error } = await supabase
        .from("playbook_nodes")
        .delete()
        .eq("id", id);
      if (error) throw error;
      return { playbookId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["playbook-nodes", data.playbookId] });
    },
  });
}
